import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  Signal,
  ViewChild
} from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router, UrlSegment } from "@angular/router";
import { combineLatest, filter, fromEvent, map, Observable, Subject, takeUntil } from "rxjs";
import { KeycloakService, KeycloakStoreService, KeycloakTokens } from "@ogs/ngx-keycloak";
import { ExtjsService } from "@core/services/extjs/extjs.service";
import { IFRAME_URL } from "@core/services/extjs/extjs.tokens";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { HTTP_ENVIRONMENT_TOKEN } from "@ogs-gmbh/ngx-http";
import { RouteMessageData } from "@core/types/extjs-message.types";
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { toSignal } from "@angular/core/rxjs-interop";
import { LocaleConfig, TranslationStoreService } from "@ogs-gmbh/ngx-translate";
import { ProcessService } from "@core/services/process/process.service";

// eslint-disable-next-line @tseslint/typedef
export const EXTJS_MESSAGE_EVENT_TYPES = {
  print: "print",
  other: "other"
} as const;
export type ExtjsPrintEvent = {
  type: typeof EXTJS_MESSAGE_EVENT_TYPES.print;
  readonly printId: string;
  /** HTTP-Status code of the ExtJS-print request */
  statusCode: number;
};
export type ExtjsOtherEvent = {
  type: typeof EXTJS_MESSAGE_EVENT_TYPES.other;
};
export type ExtjsMessageEvent = ExtjsPrintEvent | ExtjsOtherEvent;
@Component({
  selector: "app-core-page-extjs",
  templateUrl: "./extjs.component.html",
  styleUrl: "extjs.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class ExtjsComponent implements AfterViewInit, OnDestroy {
  @ViewChild("iframe") iframeQuery!: ElementRef<HTMLIFrameElement>;

  private readonly _processService: ProcessService = inject(ProcessService);

  private readonly _router: Router = inject(Router);

  private readonly _iframeBaseUrl: string = `${ inject<string>(HTTP_ENVIRONMENT_TOKEN) }/${ inject<string>(IFRAME_URL) }`;

  private _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  private readonly _extjsService: ExtjsService = inject(ExtjsService);

  private readonly _keycloakStoreService: KeycloakStoreService = inject(KeycloakStoreService);

  private readonly _keycloakService: KeycloakService = inject(KeycloakService);

  private _destroyed: Subject<void> = new Subject<void>();

  private channels: Map<string, MessageChannel> = this._extjsService.channels;

  private _wasAuthRequested: boolean = false;

  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

  private lastIFrameUrl: string | undefined;

  private _currentLocaleValue: string | null = null;

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  iframeUrl: Signal<SafeResourceUrl> = computed(() => {
    this.combinedSignal();

    const path: string = location.pathname.slice(1);

    // eslint-disable-next-line @tseslint/no-unnecessary-condition
    if (this.iframeQuery)
      this.lastIFrameUrl = path;
    // Console.log(path === this.iframeQuery.nativeElement.contentWindow?.location.href.split("#")[ 1 ]);

    return this.sanitizer.bypassSecurityTrustResourceUrl(`${ this._iframeBaseUrl }#${ this.lastIFrameUrl ?? path }`);
  });

  private readonly routeSnapshot$: Observable<ActivatedRouteSnapshot> = this._router.events.pipe(
    // eslint-disable-next-line @tseslint/no-shadow,@tseslint/typedef
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null),
    map(() => this._router.routerState.snapshot.root)
  );

  // Extract fragment
  private readonly fragment$: Observable<string | null> = this.routeSnapshot$.pipe(
    map((snapshot: ActivatedRouteSnapshot) => this.getDeepestChild(snapshot).fragment)
  );

  // Extract url segments
  private readonly url$: Observable<UrlSegment[]> = this.routeSnapshot$.pipe(
    map((snapshot: ActivatedRouteSnapshot) => snapshot.url)
  );

  // Combined observable like before
  private readonly combined$: Observable<[string | null, UrlSegment[]]> = combineLatest([
    this.fragment$,
    this.url$
  ]);

  combinedSignal: Signal<[string | null, UrlSegment[]]> = toSignal(this.combined$, { initialValue: [ null, [] ] });

  constructor () {
    effect(() => {
      const [ fragment, url ]: [string | null, UrlSegment[]] = this.combinedSignal();

      if (fragment)
        window.history.pushState({}, "", fragment.toString());
      else if (url.length) {
        const urlPath: string = url.map((segment: UrlSegment) => segment.path).join("/");

        window.history.pushState({}, "", urlPath);
      }
    });
  }

  // eslint-disable-next-line @tseslint/class-methods-use-this
  private getDeepestChild (snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let last: ActivatedRouteSnapshot | null = snapshot;

    while (last.firstChild) last = last.firstChild;

    return last;
  }

  ngAfterViewInit (): void {
    this.initMessageChannels();
    this._keycloakService.updateAccessTokenCallback = (tokens: KeycloakTokens): void => {
      this.channels.get("auth")?.port1.postMessage(tokens);
    };
    this._translationStoreService.getLocale$()
      .pipe(distinctUntilChanged((a: LocaleConfig, b: LocaleConfig) => a.value === b.value))
      .subscribe((newLocale: LocaleConfig) => {
        try {
          localStorage.setItem('language', newLocale.value.toLowerCase());
        } catch {}

        this._currentLocaleValue = newLocale.value.toLowerCase();
        this.channels.get("action")?.port1.postMessage({
          action: "reloadForLanguage",
          locale: newLocale.value
        });
      });
  }

  private initMessageChannels (): void {
    fromEvent<MessageEvent<string>>(window, "message")
      .pipe(
        takeUntil(this._destroyed),
        filter((messageEvent: MessageEvent<string>) => messageEvent.data === "initMessageChannels")
      )
      .subscribe(() => {
        this.channels = this._extjsService.channels;
        /* eslint-disable @tseslint/no-non-null-assertion */
        this.channels.get("auth")!.port1.onmessage = (): void => this.onAuthRequest();
        this.channels.get("route")!.port1.onmessage = (messageEvent: MessageEvent<RouteMessageData>): void => this.onIFrameRouteChange(messageEvent);
        this.channels.get("action")!.port1.onmessage = (messageEvent: MessageEvent<ExtjsMessageEvent>): void => this.onIFrameAction(messageEvent);
        /* eslint-enable @tseslint/no-non-null-assertion */
        // Ports an den iFrame übertragen
        this.channels.forEach((channel: MessageChannel, key: string) => {
          const msg: string = `init${ key.charAt(0).toUpperCase() + key.slice(1) }Channel`;

          this.postMessageToIFrame(msg, channel.port2);
        });

        const tokens: KeycloakTokens | null = this._keycloakStoreService.tokens;

        if (tokens)
          this.channels.get("auth")?.port1.postMessage(tokens);
      });
  }


  private onIFrameAction (messageEvent: MessageEvent<ExtjsMessageEvent>): void {
    if (messageEvent.data.type === EXTJS_MESSAGE_EVENT_TYPES.print)
      this._processService.registerProcessIdForToast(messageEvent.data.printId);
  }

  private onIFrameRouteChange (messageEvent: MessageEvent<RouteMessageData>): void {
    const { hash, forceNavigation }: RouteMessageData = messageEvent.data;

    /* TODO: wenn extjs ewig gebraucht hat cancel */
    this._extjsService.setIframeHash(hash);

    if (forceNavigation)
      void this._router.navigateByUrl(hash);
    else
      window.history.pushState({}, "", hash);
  }

  onAuthRequest (): void {
    /* eslint-disable-next-line @tseslint/no-non-null-assertion */
    const port1: MessagePort = this.channels.get("auth")!.port1;

    if (!this._wasAuthRequested) {
      this._wasAuthRequested = true;
      port1.postMessage(this._keycloakStoreService.tokens);

      return;
    }

    this._keycloakService.forceUpdateAccessToken().subscribe();
  }

  private postMessageToIFrame (message: string, channelPort: MessagePort): void {
    const iframeWindow: Window | null | undefined = this.iFrameWindow;

    if (iframeWindow)
      iframeWindow.postMessage(message, this._iframeBaseUrl, [ channelPort ]);
  }

  get iFrameWindow (): Window | null | undefined {
    return this.iframeQuery.nativeElement.contentWindow;
  }

  ngOnDestroy (): void {
    this._destroyed.next();
    this._destroyed.complete();
  }
}
